import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Swords, Plus, ChevronRight, Coins, Clock, Trophy, X } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import BottomNav from "../components/BottomNav.jsx";
import NewDuelModal from "../components/NewDuelModal.jsx";
import api from "../lib/api.js";

const STATUS_LABEL = {
  active: { label: "Live", color: "#4ade80" },
  pending: { label: "Pending", color: "#FBBF24" },
  completed: { label: "Done", color: "#a78bfa" },
  rejected: { label: "Declined", color: "#64748b" },
  cancelled: { label: "Cancelled", color: "#64748b" },
};

export default function Duels() {
  const nav = useNavigate();
  const [duels, setDuels] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/duels").then((r) => setDuels(r.data.duels));

  useEffect(() => {
    load();
    const t = setInterval(load, 25000); // live refresh
    return () => clearInterval(t);
  }, []);

  return (
    <PhoneFrame>
      <div className="ps-aurora min-h-screen">
        <div className="px-5 pt-6 pb-28 relative">
          <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="duels-back">
            <ChevronLeft size={16} /> Back
          </button>

          <div className="mt-5 ps-fade-up flex items-end justify-between">
            <div>
              <div className="ps-chip mb-3"><Swords size={11} /> Posture Duels</div>
              <h1 className="ps-heading text-4xl font-extrabold leading-[1]">
                Duels<span className="ps-serif ps-italic font-normal text-slate-500">.</span>
              </h1>
              <p className="text-slate-400 text-sm mt-2 ps-serif ps-italic">Stake points. Win the pot.</p>
            </div>
            <button
              onClick={() => setOpen(true)}
              data-testid="duels-new-btn"
              className="ps-button-primary text-sm py-2 px-4 inline-flex items-center gap-1"
            >
              <Plus size={14} /> New
            </button>
          </div>

          {duels === null ? (
            <div className="mt-10 text-center text-slate-400 ps-serif ps-italic">Loading…</div>
          ) : duels.length === 0 ? (
            <div className="mt-10 ps-card p-6 text-center">
              <Swords size={36} className="mx-auto text-slate-500" />
              <div className="mt-3 ps-heading text-lg font-extrabold">No duels yet</div>
              <div className="text-sm text-slate-400 mt-1 ps-serif ps-italic">Challenge a friend to spike your motivation.</div>
              <button onClick={() => setOpen(true)} className="ps-button-primary mt-5 inline-flex items-center gap-1.5" data-testid="duels-empty-cta">
                <Plus size={14} /> Start a duel
              </button>
            </div>
          ) : (
            <div className="mt-6 space-y-3 ps-stagger">
              {duels.map((d) => <DuelRow key={d.id} d={d} onClick={() => nav(`/duels/${d.id}`)} />)}
            </div>
          )}
        </div>
      </div>
      <BottomNav />
      <NewDuelModal open={open} onClose={() => setOpen(false)} onCreated={() => load()} />
    </PhoneFrame>
  );
}

function DuelRow({ d, onClick }) {
  const s = STATUS_LABEL[d.status] || STATUS_LABEL.pending;
  const me = d.challenger.is_me ? d.challenger : d.challenged;
  const opp = d.challenger.is_me ? d.challenged : d.challenger;
  const myLead = me.steps > opp.steps;
  const tie = me.steps === opp.steps;
  return (
    <button
      onClick={onClick}
      data-testid={`duel-row-${d.id}`}
      className="ps-card-elev p-4 w-full text-left relative overflow-hidden hover:border-[#4ade80]/40 transition-colors"
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl opacity-30" style={{ background: s.color }} />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: s.color, boxShadow: `0 0 8px ${s.color}` }} />
          <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: s.color }}>{s.label}</span>
        </div>
        <div className="text-[10px] text-slate-500 ps-tabular inline-flex items-center gap-1">
          <Coins size={10} color="#FBBF24" /> {d.stake}
          <span className="mx-1">·</span>
          <Clock size={10} color="#a78bfa" /> {d.duration_hours}h
        </div>
      </div>
      {/* head-to-head */}
      <div className="relative mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <PlayerMini u={me} side="me" lead={d.status === "active" && myLead} />
        <Swords size={18} className="text-slate-500" />
        <PlayerMini u={opp} side="opp" lead={d.status === "active" && !myLead && !tie} />
      </div>
      {d.status === "completed" && (
        <div className="relative mt-3 text-[11px] inline-flex items-center gap-1.5 font-bold" style={{ color: d.winner_id === me.id ? "#4ade80" : d.winner_id === opp.id ? "#ef4444" : "#94a3b8" }}>
          {d.winner_id === me.id ? <><Trophy size={12} /> You won +{d.stake * 2 + 50}</>
            : d.winner_id === opp.id ? <><X size={12} /> {opp.name} won</>
            : "Tied — stakes refunded"}
        </div>
      )}
      {d.status === "active" && d.ends_at && (
        <div className="relative mt-3 text-[11px] text-slate-400 ps-serif ps-italic">
          <Countdown ends={d.ends_at} />
        </div>
      )}
      <ChevronRight size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600" />
    </button>
  );
}

export function PlayerMini({ u, side, lead }) {
  return (
    <div className={`flex items-center gap-2 ${side === "opp" ? "flex-row-reverse text-right" : ""}`}>
      <div className={`w-9 h-9 rounded-full bg-gradient-to-br from-[#15303E] to-[#0E1F2A] border flex items-center justify-center font-bold text-sm ${lead ? "border-[#4ade80] ring-2 ring-[#4ade80]/30" : "border-[#1B364A]"}`}>
        {u.name?.[0]?.toUpperCase()}
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold truncate">{u.is_me ? "You" : u.name.split(" ")[0]}</div>
        <div className="text-[11px] ps-tabular font-bold" style={{ color: lead ? "#4ade80" : "#94a3b8" }}>{(u.steps || 0).toLocaleString()}</div>
      </div>
    </div>
  );
}

function Countdown({ ends }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = new Date(ends).getTime() - now;
  if (ms <= 0) return <>Finishing up…</>;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return <>{h}h {String(m).padStart(2, "0")}m {String(s).padStart(2, "0")}s remaining</>;
}
